import { z } from "zod/v4";
import { requiredVizLabelEntriesSchema } from "@/lib/ai/visualization/views/shared/label-map";
import {
  chartExportFormatsSchema,
  vizAxisPresetSchema,
  vizBarLayoutSchema,
  vizPaletteModeSchema,
  vizReferenceLineSchema,
  vizSeriesArraySchema,
  vizSeriesLabelMapSchema,
  vizTooltipModeSchema,
  vizXAxisTicksSchema,
} from "@/lib/ai/visualization/views/shared/schema-primitives";
import {
  VIZ_SORT_OPTIONS,
  VIZ_STACKING_OPTIONS,
  vizAxisSideSchema,
  vizBarComparisonYAxisModeSchema,
  vizSortSchema,
  vizStackingSchema,
} from "@/lib/ai/visualization/views/shared/view-options";

const barComparisonAuthoringBaseSchema = z
  .object({
    categoryKey: z.string().min(1),
    categoryLabel: z.string().trim().min(1),
    stacking: z.enum(VIZ_STACKING_OPTIONS),
    sort: z.enum(VIZ_SORT_OPTIONS),
  })
  .strict();

const barComparisonWideAuthoringSchema = barComparisonAuthoringBaseSchema
  .extend({
    bindingMode: z.literal("wide"),
    seriesKeys: z.array(z.string().min(1)).min(1),
    seriesLabelEntries: requiredVizLabelEntriesSchema,
  })
  .strict();

const barComparisonLongAuthoringSchema = barComparisonAuthoringBaseSchema
  .extend({
    bindingMode: z.literal("long"),
    seriesByKey: z.string().min(1),
    valueKey: z.string().min(1),
    valueLabel: z.string().trim().min(1),
  })
  .strict();

/**
 * `bar-comparison` authoring contract。
 *
 * @remarks
 * wide metrics と long-form pivot を strict branch に分け、`seriesByKey/valueKey`
 * の paired-field 補正へ依存しない generation surface にする。
 */
export const barComparisonAuthoringSchema = z.discriminatedUnion(
  "bindingMode",
  [barComparisonWideAuthoringSchema, barComparisonLongAuthoringSchema],
);

const barComparisonComponentBaseSchema = z
  .object({
    dataPath: z.string(),
    categoryKey: z.string(),
    series: vizSeriesArraySchema,
    layout: vizBarLayoutSchema.optional().nullable(),
    chartHeight: z.number().int().min(300).max(560).optional().nullable(),
    stacking: vizStackingSchema.optional().nullable(),
    sort: vizSortSchema.optional().nullable(),
    yAxisMode: vizBarComparisonYAxisModeSchema.optional().nullable(),
    seriesAxisMap: z
      .object({})
      .catchall(vizAxisSideSchema)
      .optional()
      .nullable(),
    categoryLabel: z.string().optional().nullable(),
    seriesLabels: vizSeriesLabelMapSchema,
    axisPreset: vizAxisPresetSchema,
    tooltipMode: vizTooltipModeSchema,
    paletteMode: vizPaletteModeSchema,
    xAxisTicks: vizXAxisTicksSchema,
    referenceLines: z
      .array(vizReferenceLineSchema)
      .max(6)
      .optional()
      .nullable(),
    allowSortControl: z.boolean().optional().nullable(),
    allowSeriesToggle: z.boolean().optional().nullable(),
    defaultVisibleSeriesKeys: z.array(z.string()).optional().nullable(),
    allowExport: z.boolean().optional().nullable(),
    exportFormats: chartExportFormatsSchema,
  })
  .strict();

/**
 * `BarComparison` component props schema。
 *
 * @remarks
 * LLM への誘導は structured authoring prompt で行うため、component schema は
 * dual/non-dual の形状差を宣言的な union で表現する。
 */
const barComparisonComponentNonDualSchema =
  barComparisonComponentBaseSchema.extend({
    yAxisMode: z
      .enum(["auto", "single"])
      .optional()
      .describe("Single-axis rendering mode."),
  });

const barComparisonComponentDualSchema =
  barComparisonComponentBaseSchema.extend({
    yAxisMode: z.literal("dual"),
    stacking: z
      .enum(["grouped"])
      .optional()
      .nullable()
      .describe("Dual-axis mode supports grouped bars only."),
    seriesAxisMap: z
      .object({})
      .catchall(vizAxisSideSchema)
      .describe("Map each series key to left/right axis in dual-axis mode."),
  });

export const barComparisonComponentSchema = z.union([
  barComparisonComponentNonDualSchema,
  barComparisonComponentDualSchema,
]);
